import { useState, useEffect, useCallback } from 'react'
import { ChevronDown, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import MapaCompradorPreview from './MapaCompradorPreview'
import MapaCompradorV2Preview from './MapaCompradorV2Preview'

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
      {label && <span className="text-sm text-gray-700">{label}</span>}
    </label>
  )
}

// ─── Select estilizado ────────────────────────────────────────────────────────
function Select({ label, value, onChange, children, disabled }) {
  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
        >
          {children}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function MapaComprador() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,     setLeiloes]     = useState([])
  const [compradores, setCompradores] = useState([])
  const [leilaoId,    setLeilaoId]    = useState('')
  const [compradorId, setCompradorId] = useState('')
  const [tipo,        setTipo]        = useState('padrao')
  const [versao,      setVersao]      = useState('1')
  const [dbVendedor,  setDbVendedor]  = useState(false)
  const [ordenacao,   setOrdenacao]   = useState('numero_lote')
  const [colunasV1, setColunasV1] = useState(['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'comissao', 'valor_unitario', 'total', 'total_com_comissao'])
  const [colunasV2, setColunasV2] = useState(['lote', 'curral', 'vendedor', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao'])
  const [destaqueTotalCompradores, setDestaqueTotalCompradores] = useState('liquido')
  const [ocultarTotalLiquido, setOcultarTotalLiquido] = useState(false)
  const [dbOverride,  setDbOverride]  = useState({ banco: '', agencia: '', conta: '', pix: '' })
  const [loadingLeil, setLoadingLeil] = useState(false)
  const [loadingComp, setLoadingComp] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [dados,       setDados]       = useState(null)
  const [erro,        setErro]        = useState(null)

  // ── Carrega config global + leilões ──────────────────────────────────────
  useEffect(() => {
    if (!orgId) return
    setLoadingLeil(true)
    Promise.all([
      leiloesService.getByOrganizador(orgId, { status: 'on' }),
      mapasService.getConfig(orgId).catch(() => null),
    ]).then(([list, cfg]) => {
      const sorted = [...list].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      if (sorted.length > 0) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      if (cfg) {
        setDbVendedor(!!cfg.mapa_comprador_dados_bancarios_vendedor)
        setVersao(cfg.mapa_comprador_versao_padrao || '1')
        if (cfg.mapa_comprador_v1_colunas_ativas) setColunasV1(cfg.mapa_comprador_v1_colunas_ativas)
        if (cfg.mapa_comprador_v2_colunas_ativas) setColunasV2(cfg.mapa_comprador_v2_colunas_ativas)
        setDestaqueTotalCompradores(cfg.mapa_comprador_destaque_total || 'liquido')
        setOcultarTotalLiquido(!!cfg.mapa_comprador_ocultar_total_liquido)
        setOrdenacao(cfg.mapa_comprador_ordenacao || 'numero_lote')
        setTipo(cfg.mapa_comprador_tipo_padrao === 'vendas_defesa' ? 'vendas_defesa' : 'padrao')
      }
    }).finally(() => setLoadingLeil(false))
  }, [orgId])

  // ── Carrega compradores quando leilão ou tipo muda ────────────────────────────────
  useEffect(() => {
    if (!orgId || !leilaoId) { setCompradores([]); return }
    setLoadingComp(true)
    setDados(null)
    // Mapeia o tipo do MapaComprador para o tipo do backend
    const tipoBackend = tipo === 'padrao' ? 'compras' : 'compras_ou_defesas'
    mapasService.getCompradores(orgId, leilaoId, { tipo: tipoBackend })
      .then(list => { setCompradores(list); setCompradorId('') })
      .catch(() => setCompradores([]))
      .finally(() => setLoadingComp(false))
  }, [orgId, leilaoId, tipo])

  // ── Busca dados do mapa ───────────────────────────────────────────────────
  const fetchDados = useCallback(async (cId, t, ord) => {
    if (!orgId || !leilaoId) return
    setLoadingPrev(true)
    setDados(null)
    setErro(null)
    try {
      const result = await mapasService.getMapaCompradorDados(orgId, leilaoId, {
        compradorId: cId || undefined,
        tipo: t,
        ordenacao: ord || 'numero_lote',
      })
      setDados(result)
    } catch (err) {
      setErro(err.response?.data?.detail || err.message || 'Erro ao carregar dados')
    } finally {
      setLoadingPrev(false)
    }
  }, [orgId, leilaoId])

  // ── Auto-fetch ao mudar comprador ou tipo ─────────────────────────────────
  useEffect(() => {
    if (!orgId || !leilaoId || loadingComp) return
    fetchDados(compradorId, tipo, ordenacao)
  }, [compradorId, tipo, ordenacao, leilaoId, orgId, loadingComp]) // eslint-disable-line

  const handlePesquisar = () => fetchDados(compradorId, tipo)

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o Mapa do Comprador.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-cyan-600" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa do Comprador</h1>
        {loadingPrev && <RefreshCw className="w-4 h-4 animate-spin text-blue-500 ml-1" />}
      </div>

      {/* ── Filtros — linha única compacta ─────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">

          {/* Leilão */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Leilão" value={leilaoId}
              onChange={v => { setLeilaoId(v); setDados(null) }}
              disabled={loadingLeil}>
              {loadingLeil
                ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)
              }
            </Select>
          </div>

          {/* Comprador */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Comprador" value={compradorId}
              onChange={v => { setCompradorId(v); setDados(null) }}
              disabled={loadingComp || !leilaoId}>
              <option value="">TODOS OS COMPRADORES</option>
              {compradores.map(c => (
                <option key={c.id} value={String(c.id)}>{c.nome}</option>
              ))}
            </Select>
          </div>

          {/* Tipo */}
          <div className="flex-[1.5] min-w-[130px]">
            <Select label="Tipo" value={tipo}
              onChange={v => { setTipo(v); setDados(null) }}>
              <option value="padrao">SÓ COMPRAS</option>
              <option value="vendas_defesa">COMPRAS E DEFESAS</option>
            </Select>
          </div>

          {/* Ordenação */}
          <div className="flex-[1.5] min-w-[130px]">
            <Select label="Ordenação" value={ordenacao}
              onChange={v => setOrdenacao(v)}>
              <option value="numero_lote">Nº do Lote</option>
              <option value="nome_vendedor">Nome do Vendedor</option>
            </Select>
          </div>

          {/* Versão */}
          <div className="flex-1 min-w-[100px]">
            <Select label="Versão" value={versao}
              onChange={v => { setVersao(v); setDados(null) }}>
              <option value="1">Versão 1</option>
              <option value="2">Versão 2</option>
            </Select>
          </div>

          {/* Toggle dados bancários */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-500 font-medium">Dados banc. vendedor</span>
            <div className="flex items-center h-[38px] px-3 bg-gray-50 rounded-lg border border-gray-200">
              <Toggle checked={dbVendedor} onChange={setDbVendedor}
                label={dbVendedor ? 'Sim' : 'Não'} />
            </div>
          </div>

        </div>
      </div>

      {/* ── Erro ─────────────────────────────────────────────────────────── */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {erro}
        </div>
      )}

      {/* ── Preview com scroll horizontal no mobile ──────────────────────── */}
      {dados && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          {versao === '2' ? (
            <MapaCompradorV2Preview
              dados={dados}
              showDbVendedor={dbVendedor}
              colunasAtivas={colunasV2}
              destaqueTotalCompradores={destaqueTotalCompradores}
              ocultarTotalLiquido={ocultarTotalLiquido}
              orgId={orgId}
              leilaoId={leilaoId}
              compradorId={compradorId}
              tipo={tipo}
              ordenacao={ordenacao}
              onReload={() => fetchDados(compradorId, tipo, ordenacao)}
            />
          ) : (
            <MapaCompradorPreview
              dados={dados}
              showDbVendedor={dbVendedor}
              colunasAtivas={colunasV1}
              destaqueTotalCompradores={destaqueTotalCompradores}
              ocultarTotalLiquido={ocultarTotalLiquido}
              dbOverride={dbOverride}
              orgId={orgId}
              leilaoId={leilaoId}
              compradorId={compradorId}
              tipo={tipo}
              ordenacao={ordenacao}
              onReload={() => fetchDados(compradorId, tipo, ordenacao)}
            />
          )}
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {!dados && !loadingPrev && !erro && leilaoId && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400 text-sm">
          Selecione um comprador acima para visualizar o mapa.
        </div>
      )}
    </div>
  )
}
